import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { LoaderCircle } from 'lucide-react'
import { useAccount } from '../../hooks/account-context'

/* What sits between /account and the pages under it.

   A courtesy, not the lock. Every request those pages make is answered by an
   API that reads the customer out of the token and refuses anything without
   one, so somebody who edits their way past this sees empty lists rather
   than another buyer's orders. What it buys is that the page does not spend
   its first second firing four requests that are all going to come back 401.

   `from` is remembered so a link straight to the instalments tab lands there
   after the password rather than on the account's front page. */

export function RequireCustomer() {
  const { status, can } = useAccount()
  const location = useLocation()

  if (status === 'checking') {
    return (
      <div className="grid min-h-[70svh] place-items-center">
        <LoaderCircle
          aria-label="Loading your account"
          className="size-6 animate-spin text-dim"
          strokeWidth={2}
        />
      </div>
    )
  }

  if (status === 'signed-out') {
    return (
      <Navigate to="/sign-in" replace state={{ from: location.pathname + location.search }} />
    )
  }

  /* Signed in, and the role no longer opens this. It is the same refusal the
     API gives, said before the request rather than after four of them. */
  if (!can('account.view')) {
    return (
      <div className="mx-auto w-full max-w-[560px] px-5 py-32 text-center">
        <h1 className="font-display text-[26px] font-semibold tracking-[-0.02em] text-platinum">
          This account is not open just now
        </h1>
        <p className="mt-3 text-[14.5px] leading-relaxed text-steel">
          Your sign-in works, but it is not set up to read your records at the moment. Call the
          showroom and we will sort it out.
        </p>
      </div>
    )
  }

  return <Outlet />
}
